import { isEmpty } from '@ember/utils'
import fillMonths from 'district-ui-client/utils/student-events/fill-months'
import type { StatsScope } from 'district-ui-client/utils/student-events/stats-scope'
import type StudentEventsService from 'district-ui-client/services/student-events'
import type { NormalizedInterval } from 'date-fns'

export interface CourseProgressTotalsResponse {
  course_progress_totals_summary_by_grade: SummaryData[]
  course_progress_totals_by_grade_by_month: MonthData[]
}

interface SummaryData {
  grade_position: number
  data: [
    { category: 'total_count'; count: number },
    { category: 'student_count'; count: number },
    { category: 'student_average'; average: number },
  ]
}

interface MonthData {
  grade_position: number
  months: {
    month: string
    count: number
  }[]
}

export interface CourseProgressTotalsData {
  grade_position: number
  summary: {
    total_count: number
    student_count: number
    student_average: number
  }
  months: {
    month: string
    count: number
  }[]
}

export async function courseProgressTotals(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs,
  precinct: LegacyPrecinctLessons | LegacyPrecinctMyLessons | LegacyPrecinctReading | LegacyPrecinctSpelling,
  statsScope: StatsScope,
  period: NormalizedInterval,
): Promise<Nullable<CourseProgressTotalsData[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('course_progress_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period, { precinct })

  const data = await this.fetchData<CourseProgressTotalsResponse>(url, query)
  if (!data) return null

  const { course_progress_totals_summary_by_grade: summaryData, course_progress_totals_by_grade_by_month: monthData } =
    data

  if (monthData.length === 0) {
    return null
  }

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(
  summaryData: SummaryData[],
  monthData: MonthData[],
  period: NormalizedInterval,
): CourseProgressTotalsData[] {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      const total_count = summary.data.find((value) => value.category === 'total_count')?.count ?? 0
      const student_count = summary.data.find((value) => value.category === 'student_count')?.count ?? 0
      const student_average = summary.data.find((value) => value.category === 'student_average')?.average ?? 0
      const filledMonths = fillMonths(gradeMonthData.months, period, {
        count: 0,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          total_count,
          student_count,
          student_average,
        },
        months: filledMonths,
      }
    })
    .filter((data) => data !== null)
}
